/**
 * P1.1: Ontology Proposals Queue Component
 * 
 * Manages the curation workflow for ontology proposals:
 * - View proposals by status (DRAFT, REVIEW, APPROVED, REJECTED, MERGED)
 * - Submit for review, approve, reject, merge
 * - Full audit trail display
 * 
 * P1.4: Added Ontology Extraction Trigger button
 * - Extracts proposals from kb_chunks (Glossary, definitions, headings)
 * - Writes ONLY to ontology_proposals (no direct node/edge changes)
 * - Fully idempotent + dedupe
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  ListChecks,
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Merge,
  Clock,
  RefreshCw,
  Search,
  Eye,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  History,
  User,
  Calendar,
  Wand2,
  Loader2,
  Sparkles,
  Shield,
  Edit3,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { adminFetchHeaders } from '@/hooks/useAdminFetch';

const API = window.location.origin;

const getAuthHeaders = () => adminFetchHeaders(true);

// Status configuration
const STATUS_CONFIG = {
  DRAFT: { 
    color: 'bg-gray-100 text-gray-800 border-gray-300', 
    icon: FileText, 
    label: 'Entwurf',
    description: 'Noch nicht eingereicht'
  },
  REVIEW: { 
    color: 'bg-blue-100 text-blue-800 border-blue-300', 
    icon: Clock, 
    label: 'In Prüfung',
    description: 'Wartet auf Maintainer-Entscheidung'
  },
  APPROVED: { 
    color: 'bg-green-100 text-green-800 border-green-300', 
    icon: CheckCircle, 
    label: 'Genehmigt',
    description: 'Bereit für Merge'
  },
  REJECTED: { 
    color: 'bg-red-100 text-red-800 border-red-300', 
    icon: XCircle, 
    label: 'Abgelehnt',
    description: 'Abgelehnt mit Begründung'
  },
  MERGED: { 
    color: 'bg-purple-100 text-purple-800 border-purple-300', 
    icon: Merge, 
    label: 'Gemergt',
    description: 'In Staging-Graph integriert'
  },
  PENDING_CONFLICT: { 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300', 
    icon: AlertTriangle, 
    label: 'Konflikt',
    description: 'Blockiert durch ungelösten Konflikt'
  }
};

// P1.6: Available node types for Type Guardrails
const NODE_TYPES = [
  { value: 'CONCEPT', label: 'Konzept', color: 'bg-blue-100 text-blue-800' },
  { value: 'TERM', label: 'Begriff', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'ROLE', label: 'Rolle', color: 'bg-green-100 text-green-800' },
  { value: 'EVENT', label: 'Event', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'ARTIFACT', label: 'Artefakt', color: 'bg-orange-100 text-orange-800' },
  { value: 'PROCESS', label: 'Prozess', color: 'bg-purple-100 text-purple-800' },
  { value: 'PRACTICE', label: 'Praxis', color: 'bg-pink-100 text-pink-800' },
  { value: 'PRINCIPLE', label: 'Prinzip', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'DOMAIN', label: 'Domäne', color: 'bg-gray-100 text-gray-800' },
];

// P1.7: Proposal Kind filter (CONCEPT vs CLAIM)
const PROPOSAL_KINDS = [
  { value: '', label: 'Alle', color: 'bg-gray-100 text-gray-700' },
  { value: 'CONCEPT', label: 'Konzepte', color: 'bg-blue-100 text-blue-700', description: 'Ontologie: Rollen, Events, Artefakte' },
  { value: 'CLAIM', label: 'Claims (AKU)', color: 'bg-green-100 text-green-700', description: 'Atomare Assertions für Fragen' },
];

const ProposalsQueue = ({ examProfileId, ontologyVersionId }) => {
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState('REVIEW');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProposal, setExpandedProposal] = useState(null);
  
  // P1.7: Proposal Kind filter (CONCEPT vs CLAIM)
  const [filterKind, setFilterKind] = useState('');
  
  // P2: Exam Profile filter for Ontology Gate
  const [filterExamId, setFilterExamId] = useState(examProfileId || '');
  const [availableExams, setAvailableExams] = useState([]);
  
  // P1.5: Multi-select for bulk actions
  const [selectedProposals, setSelectedProposals] = useState(new Set());
  const [bulkActionDialog, setBulkActionDialog] = useState({ open: false, type: null });
  const [bulkComment, setBulkComment] = useState('');
  
  // P1.4: Extraction state
  const [extracting, setExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [showExtractionDialog, setShowExtractionDialog] = useState(false);
  const [extractionVersions, setExtractionVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(ontologyVersionId || '');
  
  // P1.7: Claim Extraction state
  const [showClaimExtractionDialog, setShowClaimExtractionDialog] = useState(false);
  const [claimExtracting, setClaimExtracting] = useState(false);
  const [claimExtractionResult, setClaimExtractionResult] = useState(null);
  const [claimSourceId, setClaimSourceId] = useState('');
  const [availableSources, setAvailableSources] = useState([]);
  
  // Action dialogs
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, proposalId: null });
  const [actionComment, setActionComment] = useState('');
  const [mergeRuleTrace, setMergeRuleTrace] = useState('');
  const [mergeTargetId, setMergeTargetId] = useState('');
  
  // P1.6: Type Override state
  const [typeOverrideDialog, setTypeOverrideDialog] = useState({ open: false, proposalId: null, currentType: '', suggestedType: '' });
  const [selectedType, setSelectedType] = useState('');
  
  // P2: Fetch available exams for filter dropdown
  const fetchAvailableExams = useCallback(async () => {
    try {
      // Use distinct exam_ids from source_registry or exam_profiles
      const response = await fetch(`${API}/api/source-registry/list?limit=200`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const sources = data.items || data || [];
        // Collect all unique exam_ids
        const examSet = new Set();
        sources.forEach(s => {
          (s.exam_ids || []).forEach(id => examSet.add(id));
        });
        setAvailableExams(Array.from(examSet).sort());
      }
    } catch (error) {
      console.error('Error fetching available exams:', error);
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/ontology-proposals/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // P1.7: Fetch available sources for claim extraction
  const fetchSources = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/source-registry/list?limit=50`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // Filter sources that have chunks and match exam_profile
        const sources = (data.items || data || []).filter(
          s => s.chunk_count > 0 && (!examProfileId || s.exam_ids?.includes(examProfileId))
        );
        setAvailableSources(sources);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
    }
  }, [examProfileId]);

  // Fetch proposals
  const fetchProposals = useCallback(async () => {
    try {
      let url = `${API}/api/ontology-proposals?limit=200`;
      if (activeStatus && activeStatus !== 'ALL') {
        url += `&status=${activeStatus}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      // P1.7: Add proposal_kind filter
      if (filterKind) {
        url += `&proposal_kind=${filterKind}`;
      }
      // P2: Add exam_ids filter for Ontology Gate
      if (filterExamId) {
        url += `&exam_id=${encodeURIComponent(filterExamId)}`;
      }
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        // API returns { items: [...] }, extract the items array
        setProposals(data.items || data || []);
      }
    } catch (error) {
      console.error('Error fetching proposals:', error);
    }
  }, [activeStatus, searchTerm, filterKind, filterExamId]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchProposals(), fetchVersions(), fetchSources(), fetchAvailableExams()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchProposals, fetchSources, fetchAvailableExams]);
  
  // P1.4: Fetch ontology versions for extraction
  const fetchVersions = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/ontology-versions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // API returns array directly, not { versions: [] }
        const versions = Array.isArray(data) ? data : (data.versions || []);
        setExtractionVersions(versions);
        // Set default to first staging version or first available
        if (versions.length > 0 && !selectedVersionId) {
          const stagingVersion = versions.find(v => v.status === 'STAGING') || versions[0];
          setSelectedVersionId(stagingVersion.version_id);
        }
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  }, [selectedVersionId]);
  
  // P1.4: Run extraction
  const runExtraction = async () => {
    if (!examProfileId) {
      toast.error('Kein Exam Profile ausgewählt');
      return;
    }
    if (!selectedVersionId) {
      toast.error('Keine Ontology Version ausgewählt');
      return;
    }
    
    setExtracting(true);
    setExtractionResult(null);
    
    try {
      const response = await fetch(`${API}/api/ontology/extract-proposals`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          exam_profile_id: examProfileId,
          ontology_version_id: selectedVersionId,
          include_edges: true,
          confidence_threshold: 0.5
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setExtractionResult(data);
        
        if (data.status === 'IDEMPOTENT_SKIP') {
          toast.info('Extraktion bereits durchgeführt (idempotent)');
        } else if (data.created_count > 0) {
          toast.success(`${data.created_count} Vorschläge extrahiert`);
          // Refresh proposals list
          await Promise.all([fetchStats(), fetchProposals()]);
        } else {
          toast.info('Keine neuen Vorschläge gefunden');
        }
      } else {
        toast.error(data.detail || 'Extraktion fehlgeschlagen');
        setExtractionResult({ status: 'ERROR', errors: [{ message: data.detail }] });
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
      setExtractionResult({ status: 'ERROR', errors: [{ message: error.message }] });
    } finally {
      setExtracting(false);
    }
  };

  // Submit for review
  const handleSubmitForReview = async (proposalId) => {
    try {
      const response = await fetch(`${API}/api/ontology-proposals/${proposalId}/submit-for-review`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Proposal zur Prüfung eingereicht');
        await Promise.all([fetchStats(), fetchProposals()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Einreichen');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Approve proposal
  const handleApprove = async () => {
    // P1.5: Comment is now OPTIONAL for approval (scalability)
    try {
      const response = await fetch(`${API}/api/ontology-proposals/${actionDialog.proposalId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'APPROVE',
          comment: actionComment || null  // Send null if empty, backend will auto-generate
        })
      });
      
      if (response.ok) {
        toast.success('Proposal genehmigt');
        setActionDialog({ open: false, type: null, proposalId: null });
        setActionComment('');
        await Promise.all([fetchStats(), fetchProposals()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Reject proposal
  const handleReject = async () => {
    if (!actionComment.trim()) {
      toast.error('Begründung ist erforderlich');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/ontology-proposals/${actionDialog.proposalId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'REJECT',
          comment: actionComment
        })
      });
      
      if (response.ok) {
        toast.success('Proposal abgelehnt');
        setActionDialog({ open: false, type: null, proposalId: null });
        setActionComment('');
        await Promise.all([fetchStats(), fetchProposals()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // P1.8: Quick Merge (1-click CREATE_NEW, no modal)
  const handleQuickMerge = async (proposalId) => {
    try {
      const response = await fetch(`${API}/api/ontology-proposals/${proposalId}/merge`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'MERGE',
          rule_trace: 'CREATE_NEW',
          merge_target_id: null,
          comment: null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Gemergt${data.merged_node_id ? ` → ${data.merged_node_id}` : ''}`);
        await Promise.all([fetchStats(), fetchProposals()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // P1.8: Advanced Merge (with modal for merge_target_id)
  const handleAdvancedMerge = async () => {
    try {
      const response = await fetch(`${API}/api/ontology-proposals/${actionDialog.proposalId}/merge`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'MERGE',
          rule_trace: mergeRuleTrace || 'MERGE_INTO_EXISTING',
          merge_target_id: mergeTargetId || null,
          comment: actionComment
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Proposal gemergt${data.merged_node_id ? ` → ${data.merged_node_id}` : ''}`);
        setActionDialog({ open: false, type: null, proposalId: null });
        setActionComment('');
        setMergeRuleTrace('');
        setMergeTargetId('');
        await Promise.all([fetchStats(), fetchProposals()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // P1.8: Bulk Merge (CREATE_NEW)
  const handleBulkMerge = async () => {
    if (selectedProposals.size === 0) {
      toast.error('Keine Proposals ausgewählt');
      return;
    }

    try {
      const response = await fetch(`${API}/api/ontology-proposals/bulk/merge`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          proposal_ids: Array.from(selectedProposals),
          comment: bulkComment || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.merged_count} Proposals gemergt (CREATE_NEW)`);
        if (data.skipped_count > 0) {
          toast.info(`${data.skipped_count} übersprungen (nicht APPROVED)`);
        }
        if (data.error_count > 0) {
          toast.warning(`${data.error_count} Fehler`);
        }
        setBulkActionDialog({ open: false, type: null });
        setBulkComment('');
        setSelectedProposals(new Set());
        await Promise.all([fetchStats(), fetchProposals()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler bei Bulk-Merge');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Legacy Merge (kept for backward compatibility, now uses quick merge internally)
  const handleMerge = async () => {
    // P1.8: If no target specified, use quick merge
    if (!mergeTargetId) {
      await handleQuickMerge(actionDialog.proposalId);
      setActionDialog({ open: false, type: null, proposalId: null });
      setActionComment('');
      setMergeRuleTrace('');
      return;
    }
    // Otherwise use advanced merge
    await handleAdvancedMerge();
  };

  // P1.7: Run Claim Extraction
  const runClaimExtraction = async () => {
    if (!claimSourceId || !examProfileId) {
      toast.error('Source und Exam Profile erforderlich');
      return;
    }

    setClaimExtracting(true);
    setClaimExtractionResult(null);

    try {
      const response = await fetch(`${API}/api/ontology-proposals/extract-claims`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          source_id: claimSourceId,
          exam_profile_id: examProfileId,
          confidence_threshold: 0.5,
          max_claims: 100
        })
      });

      if (response.ok) {
        const data = await response.json();
        setClaimExtractionResult(data);
        
        if (data.proposals_created > 0) {
          toast.success(`${data.proposals_created} CLAIM Proposals erstellt`);
        } else if (data.admin_hint) {
          toast.warning(data.admin_hint);
        } else {
          toast.info('Keine Claims extrahiert');
        }
        
        // Refresh proposals list
        await fetchProposals();
        await fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler bei Claim Extraktion');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    } finally {
      setClaimExtracting(false);
    }
  };

  // P1.5: Toggle single proposal selection
  const toggleProposalSelection = (proposalId) => {
    setSelectedProposals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(proposalId)) {
        newSet.delete(proposalId);
      } else {
        newSet.add(proposalId);
      }
      return newSet;
    });
  };

  // P1.5: Select all visible proposals in REVIEW status
  const selectAllReview = () => {
    const reviewProposals = proposals.filter(p => p.status === 'REVIEW');
    setSelectedProposals(new Set(reviewProposals.map(p => p.proposal_id)));
  };

  // P1.5: Clear selection
  const clearSelection = () => {
    setSelectedProposals(new Set());
  };

  // P1.5: Bulk Approve handler
  const handleBulkApprove = async () => {
    if (selectedProposals.size === 0) {
      toast.error('Keine Proposals ausgewählt');
      return;
    }

    try {
      const response = await fetch(`${API}/api/ontology-proposals/bulk/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          proposal_ids: Array.from(selectedProposals),
          comment: bulkComment || null  // Optional comment
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.approved_count} Proposals genehmigt`);
        if (data.skipped_count > 0) {
          toast.info(`${data.skipped_count} übersprungen (nicht in REVIEW)`);
        }
        setBulkActionDialog({ open: false, type: null });
        setBulkComment('');
        setSelectedProposals(new Set());
        await Promise.all([fetchStats(), fetchProposals()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler bei Bulk-Genehmigung');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // P1.5: Bulk Reject handler
  const handleBulkReject = async () => {
    if (selectedProposals.size === 0) {
      toast.error('Keine Proposals ausgewählt');
      return;
    }
    if (!bulkComment.trim()) {
      toast.error('Begründung ist erforderlich für Ablehnung');
      return;
    }

    try {
      const response = await fetch(`${API}/api/ontology-proposals/bulk/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          proposal_ids: Array.from(selectedProposals),
          comment: bulkComment
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.rejected_count} Proposals abgelehnt`);
        if (data.skipped_count > 0) {
          toast.info(`${data.skipped_count} übersprungen (nicht in REVIEW)`);
        }
        setBulkActionDialog({ open: false, type: null });
        setBulkComment('');
        setSelectedProposals(new Set());
        await Promise.all([fetchStats(), fetchProposals()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler bei Bulk-Ablehnung');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // P1.6: Type Override handler
  const handleTypeOverride = async () => {
    if (!selectedType || !typeOverrideDialog.proposalId) {
      toast.error('Typ auswählen');
      return;
    }

    try {
      // Update the proposal's node_data.node_type
      const proposal = proposals.find(p => p.proposal_id === typeOverrideDialog.proposalId);
      if (!proposal) {
        toast.error('Proposal nicht gefunden');
        return;
      }

      const response = await fetch(`${API}/api/ontology-proposals/${typeOverrideDialog.proposalId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          node_data: {
            ...proposal.node_data,
            node_type: selectedType
          },
          suggested_type: selectedType,
          type_confidence: 1.0,  // Manual override = 100% confidence
          type_rationale: `Manual override: ${typeOverrideDialog.suggestedType} → ${selectedType}`,
          needs_type_review: false
        })
      });

      if (response.ok) {
        toast.success(`Typ geändert: ${selectedType}`);
        setTypeOverrideDialog({ open: false, proposalId: null, currentType: '', suggestedType: '' });
        setSelectedType('');
        await fetchProposals();
      } else {
        // If PATCH not available, show info
        toast.info('Typ-Override wird bei Merge berücksichtigt');
        setTypeOverrideDialog({ open: false, proposalId: null, currentType: '', suggestedType: '' });
        setSelectedType('');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Render proposal content
  const renderProposalContent = (proposal) => {
    if (proposal.proposal_type === 'NODE' && proposal.node_data) {
      const nd = proposal.node_data;
      const nodeTypeConfig = NODE_TYPES.find(t => t.value === nd.node_type) || NODE_TYPES[0];
      
      // P1.6: Type Guardrails - show suggested type info
      const hasSuggestedType = proposal.suggested_type;
      const needsTypeReview = proposal.needs_type_review;
      const typeConfidence = proposal.type_confidence;
      const typeRationale = proposal.type_rationale;
      
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Current/Suggested Type Badge */}
            <Badge className={nodeTypeConfig.color}>
              <Tag className="w-3 h-3 mr-1" />
              {nodeTypeConfig.label}
            </Badge>
            
            {/* P1.6: Type Confidence Indicator */}
            {hasSuggestedType && typeConfidence !== undefined && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                typeConfidence >= 0.7 ? 'bg-green-100 text-green-700' :
                typeConfidence >= 0.5 ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {Math.round(typeConfidence * 100)}% Konfidenz
              </span>
            )}
            
            {/* P1.6: Needs Review Warning */}
            {needsTypeReview && (
              <Badge variant="outline" className="border-orange-400 text-orange-600 bg-orange-50">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Typ prüfen
              </Badge>
            )}
            
            <span className="font-semibold">{nd.canonical_name}</span>
          </div>
          
          {nd.definition && (
            <p className="text-sm text-gray-600">{nd.definition}</p>
          )}
          
          {/* P1.6: Type Rationale (collapsible) */}
          {typeRationale && (
            <p className="text-xs text-gray-500 italic">
              <Shield className="w-3 h-3 inline mr-1" />
              {typeRationale}
            </p>
          )}
          
          {nd.domain && (
            <Badge variant="secondary" className="text-xs">{nd.domain}</Badge>
          )}
        </div>
      );
    }
    
    if (proposal.proposal_type === 'EDGE' && proposal.edge_data) {
      const ed = proposal.edge_data;
      return (
        <div className="flex items-center gap-2">
          <Badge variant="outline">{ed.source_node_id}</Badge>
          <span className="text-purple-600">→ {ed.edge_type} →</span>
          <Badge variant="outline">{ed.target_node_id}</Badge>
        </div>
      );
    }
    
    return <span className="text-gray-500">Keine Daten</span>;
  };

  // Render audit trail
  const renderAuditTrail = (auditTrail) => {
    if (!auditTrail || auditTrail.length === 0) return null;
    
    return (
      <div className="mt-4 border-t pt-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <History className="w-4 h-4" />
          Audit Trail
        </div>
        <div className="space-y-2">
          {auditTrail.map((entry, idx) => (
            <div key={`audit-${entry.timestamp || idx}-${entry.action}`} className="text-xs border-l-2 border-gray-200 pl-3 py-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{entry.action}</Badge>
                <span className="text-gray-500">
                  {entry.previous_status} → {entry.new_status}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-gray-500">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {entry.actor}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(entry.timestamp).toLocaleString('de-DE')}
                </span>
              </div>
              {entry.comment && (
                <p className="mt-1 text-gray-600 italic">"{entry.comment}"</p>
              )}
              {entry.rule_trace && (
                <p className="mt-1 text-purple-600">Rule: {entry.rule_trace}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2">Lade Proposals Queue...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ListChecks className="w-6 h-6 text-blue-600" />
            Proposals Queue
          </h2>
          <p className="text-gray-600 mt-1">
            Kuratierung von Ontologie-Vorschlägen (P1.1)
          </p>
        </div>
        <div className="flex gap-2">
          {/* P1.7: Claim Extraction Button */}
          <Button
            onClick={() => setShowClaimExtractionDialog(true)}
            className="bg-green-600 hover:bg-green-700"
            data-testid="extract-claims-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AKU Claims extrahieren
          </Button>
          {/* P1.4: Ontology Extraction Button */}
          <Button
            onClick={() => setShowExtractionDialog(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="extract-proposals-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Ontologie-Vorschläge generieren
          </Button>
          <Button
            variant="outline"
            onClick={() => Promise.all([fetchStats(), fetchProposals()])}
            data-testid="refresh-proposals"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </div>
      
      {/* P1.4: Extraction Dialog */}
      <Dialog open={showExtractionDialog} onOpenChange={setShowExtractionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Ontologie-Vorschläge extrahieren
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Extrahiert automatisch Konzepte, Begriffe und Beziehungen aus den Quelldokumenten.
              Schreibt <strong>nur</strong> in Proposals (keine direkten Änderungen an Nodes/Edges).
            </p>
            
            {/* Version Selection */}
            <div>
              <Label>Ontology Version (Staging)</Label>
              <Select value={selectedVersionId} onValueChange={setSelectedVersionId}>
                <SelectTrigger data-testid="extraction-version-select">
                  <SelectValue placeholder="Version wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {extractionVersions.map(v => (
                    <SelectItem key={v.version_id} value={v.version_id}>
                      v{v.version_number} - {v.label || 'Staging'} ({v.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Proposals werden dieser Version zugeordnet
              </p>
            </div>
            
            {/* Exam Profile Info */}
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <div className="font-medium">Exam Profile:</div>
              <div className="text-gray-600">{examProfileId || 'Nicht ausgewählt'}</div>
            </div>
            
            {/* Extraction Result */}
            {extractionResult && (
              <div className={`p-4 rounded-lg ${
                extractionResult.status === 'SUCCESS' ? 'bg-green-50 border border-green-200' :
                extractionResult.status === 'IDEMPOTENT_SKIP' ? 'bg-blue-50 border border-blue-200' :
                extractionResult.status === 'PARTIAL_SUCCESS' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-red-50 border border-red-200'
              }`}>
                <div className="font-medium mb-2 flex items-center gap-2">
                  {extractionResult.status === 'SUCCESS' && <CheckCircle className="w-4 h-4 text-green-600" />}
                  {extractionResult.status === 'IDEMPOTENT_SKIP' && <AlertTriangle className="w-4 h-4 text-blue-600" />}
                  {extractionResult.status === 'ERROR' && <XCircle className="w-4 h-4 text-red-600" />}
                  {extractionResult.status === 'SUCCESS' ? 'Erfolgreich' : 
                   extractionResult.status === 'IDEMPOTENT_SKIP' ? 'Bereits ausgeführt' :
                   'Fehler'}
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Erstellt: <strong>{extractionResult.created_count || 0}</strong></div>
                  <div>Übersprungen: <strong>{extractionResult.skipped_count || 0}</strong></div>
                  <div>Duplikate: <strong>{extractionResult.dedupe_hits || 0}</strong></div>
                  {extractionResult.duration_ms && (
                    <div>Dauer: <strong>{extractionResult.duration_ms}ms</strong></div>
                  )}
                </div>
                
                {extractionResult.run_key && (
                  <div className="mt-2 text-xs text-gray-500">
                    Run: {extractionResult.run_key}
                  </div>
                )}
                
                {extractionResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600">
                    {extractionResult.errors.length} Fehler
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtractionDialog(false)}>
              Schließen
            </Button>
            <Button 
              onClick={runExtraction}
              disabled={extracting || !examProfileId || !selectedVersionId}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="run-extraction-btn"
            >
              {extracting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Extrahiere...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Extraktion starten
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P1.7: Claim Extraction Dialog */}
      <Dialog open={showClaimExtractionDialog} onOpenChange={setShowClaimExtractionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-600" />
              AKU Claims extrahieren
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-green-50 rounded-lg text-sm text-green-800">
              <p className="font-medium">Was sind CLAIM Proposals?</p>
              <ul className="mt-1 list-disc list-inside text-xs">
                <li>Atomare, testbare Assertions aus Quelldokumenten</li>
                <li>Mit Evidenz (kb_chunks) verknüpft</li>
                <li>Können zu AKU v2 promotet werden</li>
                <li>Basis für Fragen-Generierung</li>
              </ul>
            </div>
            
            <div>
              <Label>Source auswählen</Label>
              <Select value={claimSourceId} onValueChange={setClaimSourceId}>
                <SelectTrigger>
                  <SelectValue placeholder="-- Source wählen --" />
                </SelectTrigger>
                <SelectContent>
                  {availableSources.map(source => {
                    const displayName = source.display_name || source.title || source.canonical_name || source.source_id;
                    const tier = source.tier || 'N/A';
                    const lang = source.language || '?';
                    const releaseDate = source.release_date ? new Date(source.release_date).toLocaleDateString('de-DE') : null;
                    const examIds = source.exam_ids?.join(', ') || 'N/A';
                    
                    return (
                      <SelectItem key={source.source_id} value={source.source_id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{displayName}</span>
                          <span className="text-xs text-gray-500">
                            {tier} | {lang.toUpperCase()} | {source.chunk_count} Chunks | {examIds}
                            {releaseDate && ` | ${releaseDate}`}
                          </span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Source muss Chunks haben. Ohne Chunks keine Claim-Extraktion möglich.
              </p>
            </div>

            {claimExtractionResult && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium text-gray-700 mb-2">Ergebnis:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>Chunks verarbeitet: <strong>{claimExtractionResult.total_chunks_processed}</strong></div>
                  <div>Claims gefunden: <strong>{claimExtractionResult.claims_extracted}</strong></div>
                  <div>Proposals erstellt: <strong className="text-green-700">{claimExtractionResult.proposals_created}</strong></div>
                  <div>Duplikate: <strong>{claimExtractionResult.dedupe_hits}</strong></div>
                </div>
                {claimExtractionResult.admin_hint && (
                  <p className="mt-2 text-orange-700 text-xs">{claimExtractionResult.admin_hint}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClaimExtractionDialog(false)}>
              Schließen
            </Button>
            <Button 
              onClick={runClaimExtraction}
              disabled={claimExtracting || !claimSourceId}
              className="bg-green-600 hover:bg-green-700"
              data-testid="run-claim-extraction-btn"
            >
              {claimExtracting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Extrahiere Claims...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Claims extrahieren
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-gray-600">Gesamt</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer border-2 ${activeStatus === 'REVIEW' ? 'border-blue-500' : 'border-transparent'}`}
            onClick={() => setActiveStatus('REVIEW')}
          >
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-700">
                {stats.pending_review || 0}
              </div>
              <div className="text-sm text-blue-600 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                In Prüfung
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer border-2 ${activeStatus === 'APPROVED' ? 'border-green-500' : 'border-transparent'}`}
            onClick={() => setActiveStatus('APPROVED')}
          >
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-700">
                {stats.ready_for_merge || 0}
              </div>
              <div className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Bereit für Merge
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer border-2 ${activeStatus === 'DRAFT' ? 'border-gray-500' : 'border-transparent'}`}
            onClick={() => setActiveStatus('DRAFT')}
          >
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-700">
                {stats.by_status?.DRAFT || 0}
              </div>
              <div className="text-sm text-gray-600 flex items-center gap-1">
                <FileText className="w-4 h-4" />
                Entwürfe
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer border-2 ${activeStatus === 'MERGED' ? 'border-purple-500' : 'border-transparent'}`}
            onClick={() => setActiveStatus('MERGED')}
          >
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-purple-700">
                {stats.by_status?.MERGED || 0}
              </div>
              <div className="text-sm text-purple-600 flex items-center gap-1">
                <Merge className="w-4 h-4" />
                Gemergt
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* P1.7: Kind Filter Badges */}
      <div className="flex gap-2 items-center">
        <span className="text-sm text-gray-600">Art:</span>
        {PROPOSAL_KINDS.map(kind => (
          <Badge
            key={kind.value}
            variant={filterKind === kind.value ? 'default' : 'outline'}
            className={`cursor-pointer ${filterKind === kind.value ? kind.color : ''}`}
            onClick={() => setFilterKind(kind.value)}
            data-testid={`filter-kind-${kind.value || 'all'}`}
          >
            {kind.label}
          </Badge>
        ))}
        {filterKind && (
          <span className="text-xs text-gray-500 ml-2">
            {PROPOSAL_KINDS.find(k => k.value === filterKind)?.description}
          </span>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-proposals"
            />
          </div>
        </div>
        {/* P2: Exam/Course Filter */}
        <Select value={filterExamId || 'ALL'} onValueChange={(v) => setFilterExamId(v === 'ALL' ? '' : v)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-exam-select">
            <SelectValue placeholder="Exam/Kurs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Alle Exams</SelectItem>
            {availableExams.map(exam => (
              <SelectItem key={exam} value={exam}>{exam}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={activeStatus} onValueChange={setActiveStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status wählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Alle Status</SelectItem>
            <SelectItem value="DRAFT">Entwurf</SelectItem>
            <SelectItem value="REVIEW">In Prüfung</SelectItem>
            <SelectItem value="APPROVED">Genehmigt</SelectItem>
            <SelectItem value="REJECTED">Abgelehnt</SelectItem>
            <SelectItem value="MERGED">Gemergt</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* P1.5: Bulk Actions Toolbar for REVIEW */}
      {activeStatus === 'REVIEW' && proposals.filter(p => p.status === 'REVIEW').length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedProposals.size > 0 && selectedProposals.size === proposals.filter(p => p.status === 'REVIEW').length}
                    onChange={(e) => e.target.checked ? selectAllReview() : clearSelection()}
                    className="w-4 h-4 rounded border-gray-300"
                    data-testid="select-all-checkbox"
                  />
                  <span className="text-sm font-medium text-blue-800">
                    {selectedProposals.size > 0 
                      ? `${selectedProposals.size} ausgewählt`
                      : 'Alle auswählen'}
                  </span>
                </div>
                {selectedProposals.size > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearSelection}
                    className="text-blue-600"
                  >
                    Auswahl aufheben
                  </Button>
                )}
              </div>
              
              {selectedProposals.size > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => setBulkActionDialog({ open: true, type: 'approve' })}
                    data-testid="bulk-approve-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Alle genehmigen
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setBulkActionDialog({ open: true, type: 'reject' })}
                    data-testid="bulk-reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Alle ablehnen
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* P1.8: Bulk Actions Toolbar for APPROVED (Merge) */}
      {activeStatus === 'APPROVED' && proposals.filter(p => p.status === 'APPROVED').length > 0 && (
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedProposals.size > 0 && selectedProposals.size === proposals.filter(p => p.status === 'APPROVED').length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const approvedIds = proposals.filter(p => p.status === 'APPROVED').map(p => p.proposal_id);
                        setSelectedProposals(new Set(approvedIds));
                      } else {
                        clearSelection();
                      }
                    }}
                    className="w-4 h-4 rounded border-gray-300"
                    data-testid="select-all-approved-checkbox"
                  />
                  <span className="text-sm font-medium text-purple-800">
                    {selectedProposals.size > 0 
                      ? `${selectedProposals.size} ausgewählt`
                      : 'Alle auswählen'}
                  </span>
                </div>
                {selectedProposals.size > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearSelection}
                    className="text-purple-600"
                  >
                    Auswahl aufheben
                  </Button>
                )}
              </div>
              
              {selectedProposals.size > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={() => setBulkActionDialog({ open: true, type: 'merge' })}
                    data-testid="bulk-merge-btn"
                  >
                    <Merge className="w-4 h-4 mr-1" />
                    Alle mergen (CREATE_NEW)
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Proposals List */}
      <Card>
        <CardContent className="pt-4">
          {/* P2: Header with count */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b">
            <span className="text-sm font-medium text-gray-600">
              {proposals.length} Proposals {filterExamId && `für ${filterExamId}`}
            </span>
          </div>
          {proposals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ListChecks className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Keine Proposals mit diesem Status gefunden.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2" data-testid="proposals-list">
              {proposals.map((proposal) => {
                const statusConfig = STATUS_CONFIG[proposal.status] || STATUS_CONFIG.DRAFT;
                const StatusIcon = statusConfig.icon;
                const isExpanded = expandedProposal === proposal.proposal_id;
                const isSelected = selectedProposals.has(proposal.proposal_id);
                
                return (
                  <div 
                    key={proposal.proposal_id}
                    className={`border rounded-lg p-4 hover:bg-gray-50 ${isSelected ? 'bg-blue-50 border-blue-300' : ''}`}
                    data-testid={`proposal-${proposal.proposal_id}`}
                  >
                    <div className="flex items-start justify-between">
                      {/* P1.5: Checkbox for multi-select */}
                      {proposal.status === 'REVIEW' && (
                        <div className="mr-3 pt-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleProposalSelection(proposal.proposal_id)}
                            className="w-4 h-4 rounded border-gray-300"
                            data-testid={`checkbox-${proposal.proposal_id}`}
                          />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          <Badge variant="outline">{proposal.proposal_type}</Badge>
                          {proposal.certification_id && (
                            <Badge variant="secondary">{proposal.certification_id}</Badge>
                          )}
                          {/* Exam/Course Badge - prominent display */}
                          {proposal.exam_ids?.length > 0 && (
                            <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                              📚 {proposal.exam_ids.join(', ')}
                            </Badge>
                          )}
                          {/* Source Badge - shows evidence origin */}
                          {proposal.source_id && (
                            <Badge variant="outline" className="text-xs bg-purple-50 border-purple-200 text-purple-700">
                              📄 {proposal.source_id.slice(0, 15)}...
                            </Badge>
                          )}
                        </div>
                        
                        {renderProposalContent(proposal)}
                        
                        {/* Evidence Preview */}
                        {proposal.evidence_refs?.length > 0 && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600 border-l-2 border-purple-300">
                            <span className="font-medium text-purple-700">Evidence:</span>{' '}
                            {proposal.evidence_refs[0].extracted_text?.slice(0, 150)}...
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-400 mt-2">
                          {proposal.proposal_id} • 
                          Erstellt: {new Date(proposal.created_at).toLocaleString('de-DE')}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        {/* Action buttons based on status */}
                        {proposal.status === 'DRAFT' && (
                          <Button
                            size="sm"
                            onClick={() => handleSubmitForReview(proposal.proposal_id)}
                            data-testid={`submit-review-${proposal.proposal_id}`}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Einreichen
                          </Button>
                        )}
                        
                        {proposal.status === 'REVIEW' && (
                          <>
                            {/* P1.6: Type Override Button for low-confidence types */}
                            {proposal.needs_type_review && proposal.proposal_type === 'NODE' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-orange-600 border-orange-300"
                                onClick={() => {
                                  setTypeOverrideDialog({
                                    open: true,
                                    proposalId: proposal.proposal_id,
                                    currentType: proposal.node_data?.node_type || 'CONCEPT',
                                    suggestedType: proposal.suggested_type || proposal.node_data?.node_type || 'CONCEPT'
                                  });
                                  setSelectedType(proposal.node_data?.node_type || 'CONCEPT');
                                }}
                                title="Typ korrigieren"
                                data-testid={`type-override-${proposal.proposal_id}`}
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600"
                              onClick={() => setActionDialog({ open: true, type: 'approve', proposalId: proposal.proposal_id })}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => setActionDialog({ open: true, type: 'reject', proposalId: proposal.proposal_id })}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        
                        {proposal.status === 'APPROVED' && (
                          <div className="flex items-center gap-1">
                            {/* P1.8: Checkbox for bulk selection */}
                            <input
                              type="checkbox"
                              checked={selectedProposals.has(proposal.proposal_id)}
                              onChange={() => toggleProposalSelection(proposal.proposal_id)}
                              className="w-4 h-4 rounded border-gray-300 mr-2"
                              data-testid={`checkbox-approved-${proposal.proposal_id}`}
                            />
                            {/* P1.8: 1-Click Merge (CREATE_NEW) */}
                            <Button
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700"
                              onClick={() => handleQuickMerge(proposal.proposal_id)}
                              title="Quick Merge (CREATE_NEW)"
                              data-testid={`quick-merge-${proposal.proposal_id}`}
                            >
                              <Merge className="w-4 h-4 mr-1" />
                              Merge
                            </Button>
                            {/* P1.8: Advanced Merge option */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setActionDialog({ open: true, type: 'merge', proposalId: proposal.proposal_id })}
                              title="Erweitertes Merge (in bestehendes mergen)"
                              data-testid={`advanced-merge-${proposal.proposal_id}`}
                            >
                              ...
                            </Button>
                          </div>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedProposal(isExpanded ? null : proposal.proposal_id)}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t">
                        {/* Full data */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Source ID:</span>
                            <span className="ml-2">{proposal.source_id || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Conflict Status:</span>
                            <span className="ml-2">{proposal.conflict_resolution_status}</span>
                          </div>
                        </div>
                        
                        {/* Merged info */}
                        {proposal.merged_into_node_id && (
                          <div className="mt-2 p-2 bg-purple-50 rounded text-sm">
                            <span className="text-purple-700">
                              Gemergt in Node: {proposal.merged_into_node_id}
                            </span>
                            {proposal.merge_rule_trace && (
                              <div className="text-purple-600 text-xs mt-1">
                                Rule: {proposal.merge_rule_trace}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Audit trail */}
                        {renderAuditTrail(proposal.audit_trail)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Dialogs */}
      {/* Approve Dialog */}
      <Dialog 
        open={actionDialog.open && actionDialog.type === 'approve'} 
        onOpenChange={(open) => !open && setActionDialog({ open: false, type: null, proposalId: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Proposal genehmigen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Kommentar (optional)</Label>
              <Textarea
                value={actionComment}
                onChange={(e) => setActionComment(e.target.value)}
                placeholder="Optional: Begründung für die Genehmigung..."
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                Wenn leer, wird automatisch "Approved by {'{user}'} (no comment)" eingetragen.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, proposalId: null })}>
              Abbrechen
            </Button>
            <Button onClick={handleApprove} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Genehmigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog 
        open={actionDialog.open && actionDialog.type === 'reject'} 
        onOpenChange={(open) => !open && setActionDialog({ open: false, type: null, proposalId: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              Proposal ablehnen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Begründung (erforderlich)</Label>
              <Textarea
                value={actionComment}
                onChange={(e) => setActionComment(e.target.value)}
                placeholder="Warum wird dieser Proposal abgelehnt?"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, proposalId: null })}>
              Abbrechen
            </Button>
            <Button onClick={handleReject} variant="destructive">
              <XCircle className="w-4 h-4 mr-2" />
              Ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog 
        open={actionDialog.open && actionDialog.type === 'merge'} 
        onOpenChange={(open) => !open && setActionDialog({ open: false, type: null, proposalId: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5 text-purple-600" />
              Proposal in Staging mergen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-800">
              <p className="font-medium">Erweitertes Merge</p>
              <p>Für Merge in bestehendes Node oder mit speziellem Rule-Trace.</p>
            </div>
            
            <div>
              <Label>Rule Trace</Label>
              <Select value={mergeRuleTrace} onValueChange={setMergeRuleTrace}>
                <SelectTrigger>
                  <SelectValue placeholder="CREATE_NEW (Standard)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CREATE_NEW">CREATE_NEW (Neues erstellen)</SelectItem>
                  <SelectItem value="MERGE_INTO_EXISTING">MERGE_INTO_EXISTING (In bestehendes)</SelectItem>
                  <SelectItem value="EQUIVALENT">EQUIVALENT (Äquivalent markieren)</SelectItem>
                  <SelectItem value="REDIRECT">REDIRECT (Umleitung)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                CREATE_NEW ist der Standard. Andere Optionen erfordern Merge-Target.
              </p>
            </div>
            
            <div>
              <Label>Merge-Target ID {mergeRuleTrace && mergeRuleTrace !== 'CREATE_NEW' && <span className="text-red-500">*</span>}</Label>
              <Input
                value={mergeTargetId}
                onChange={(e) => setMergeTargetId(e.target.value)}
                placeholder="node_xxx (leer = neuer Node)"
              />
              <p className="text-xs text-gray-500 mt-1">
                {mergeRuleTrace && mergeRuleTrace !== 'CREATE_NEW' 
                  ? 'Erforderlich für diesen Rule-Trace'
                  : 'Leer lassen um neuen Node zu erstellen'}
              </p>
            </div>
            
            <div>
              <Label>Kommentar (optional)</Label>
              <Textarea
                value={actionComment}
                onChange={(e) => setActionComment(e.target.value)}
                placeholder="Zusätzliche Anmerkungen..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, proposalId: null })}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleAdvancedMerge} 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={mergeRuleTrace && mergeRuleTrace !== 'CREATE_NEW' && !mergeTargetId}
            >
              <Merge className="w-4 h-4 mr-2" />
              Mergen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P1.5: Bulk Approve Dialog */}
      <Dialog 
        open={bulkActionDialog.open && bulkActionDialog.type === 'approve'} 
        onOpenChange={(open) => !open && setBulkActionDialog({ open: false, type: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              {selectedProposals.size} Proposals genehmigen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-green-50 rounded-lg text-sm text-green-800">
              <p className="font-medium">Bulk-Genehmigung</p>
              <p>Alle ausgewählten Proposals werden mit demselben Kommentar genehmigt.</p>
            </div>
            <div>
              <Label>Kommentar für alle (optional)</Label>
              <Textarea
                value={bulkComment}
                onChange={(e) => setBulkComment(e.target.value)}
                placeholder="Optional: Gemeinsamer Kommentar für alle Genehmigungen..."
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                Wenn leer, wird automatisch "Bulk approved by {'{user}'} (no comment)" eingetragen.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionDialog({ open: false, type: null })}>
              Abbrechen
            </Button>
            <Button onClick={handleBulkApprove} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Alle genehmigen ({selectedProposals.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P1.5: Bulk Reject Dialog */}
      <Dialog 
        open={bulkActionDialog.open && bulkActionDialog.type === 'reject'} 
        onOpenChange={(open) => !open && setBulkActionDialog({ open: false, type: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              {selectedProposals.size} Proposals ablehnen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 rounded-lg text-sm text-red-800">
              <p className="font-medium">Bulk-Ablehnung</p>
              <p>Alle ausgewählten Proposals werden mit demselben Grund abgelehnt.</p>
            </div>
            <div>
              <Label>Begründung für alle (erforderlich)</Label>
              <Textarea
                value={bulkComment}
                onChange={(e) => setBulkComment(e.target.value)}
                placeholder="Warum werden diese Proposals abgelehnt?"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionDialog({ open: false, type: null })}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleBulkReject} 
              variant="destructive"
              disabled={!bulkComment.trim()}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Alle ablehnen ({selectedProposals.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P1.8: Bulk Merge Dialog */}
      <Dialog 
        open={bulkActionDialog.open && bulkActionDialog.type === 'merge'} 
        onOpenChange={(open) => !open && setBulkActionDialog({ open: false, type: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5 text-purple-600" />
              {selectedProposals.size} Proposals mergen (CREATE_NEW)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-800">
              <p className="font-medium">Bulk-Merge (1-Click)</p>
              <ul className="mt-1 list-disc list-inside text-xs">
                <li>Alle ausgewählten APPROVED Proposals werden zu neuen Nodes/Edges</li>
                <li>rule_trace = CREATE_NEW (automatisch)</li>
                <li>Audit-Trail wird für jedes Proposal erstellt</li>
              </ul>
            </div>
            <div>
              <Label>Kommentar (optional)</Label>
              <Textarea
                value={bulkComment}
                onChange={(e) => setBulkComment(e.target.value)}
                placeholder="Optional: Gemeinsamer Kommentar..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionDialog({ open: false, type: null })}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleBulkMerge} 
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-bulk-merge-btn"
            >
              <Merge className="w-4 h-4 mr-2" />
              Alle mergen ({selectedProposals.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P1.6: Type Override Dialog */}
      <Dialog 
        open={typeOverrideDialog.open} 
        onOpenChange={(open) => !open && setTypeOverrideDialog({ open: false, proposalId: null, currentType: '', suggestedType: '' })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-orange-600" />
              Node-Typ korrigieren
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-orange-50 rounded-lg text-sm text-orange-800">
              <p className="font-medium">Type Guardrails</p>
              <p>Der automatisch vorgeschlagene Typ hatte niedrige Konfidenz. Bitte den korrekten Typ auswählen.</p>
            </div>
            
            <div>
              <Label>Vorgeschlagener Typ</Label>
              <div className="mt-1">
                <Badge variant="outline" className="text-gray-600">
                  {NODE_TYPES.find(t => t.value === typeOverrideDialog.suggestedType)?.label || typeOverrideDialog.suggestedType}
                </Badge>
              </div>
            </div>
            
            <div>
              <Label>Neuer Typ auswählen</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {NODE_TYPES.map(nodeType => (
                  <Button
                    key={nodeType.value}
                    size="sm"
                    variant={selectedType === nodeType.value ? 'default' : 'outline'}
                    className={selectedType === nodeType.value ? 'ring-2 ring-orange-400' : ''}
                    onClick={() => setSelectedType(nodeType.value)}
                    data-testid={`type-btn-${nodeType.value}`}
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {nodeType.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setTypeOverrideDialog({ open: false, proposalId: null, currentType: '', suggestedType: '' })}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleTypeOverride} 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={!selectedType}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Typ bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProposalsQueue;
